import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { AffiliateOrganization } from "schemas/dashboard";

import StyledLink from "components/Styled/Link";
import {
  AFFILIATE_TERMS_AND_CONDITIONS_LINK,
  COMMISSION_PERCENTAGE,
} from "constants/influencer";
import { fetcherAuth } from "utils/api";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

import SpecialBonuses from "../SpecialBonuses";

type AffiliateReferralCredit = {
  id: number;
  createdAt: number;
  affiliateReferralId: number;
  commissionAmountCents: number;
  bonusCents?: number;
};

type AffiliatePayout = {
  id: number;
  createdAt: number;
  date: string;
  type: "standard" | "cumulative" | "bonus";
  amountCents: number;
};

export default function AffiliatePartnerView({
  affiliateOrg,
}: {
  affiliateOrg: AffiliateOrganization;
}) {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);

  const [fetchLoading, setFetchLoading] = useState(false);
  const [affiliateCredits, setAffiliateCredits] = useState<
    AffiliateReferralCredit[]
  >([]);
  const [affiliatePayouts, setAffiliatePayouts] = useState<AffiliatePayout[]>(
    [],
  );
  const [balance, setBalance] = useState<{
    total_commission: number;
    total_bonus: number;
  }>({ total_commission: 0, total_bonus: 0 });
  const [totalPayoutsByType, setTotalPayoutsByType] = useState<{
    standard: number;
    cumulative: number;
    bonus: number;
  }>({ standard: 0, cumulative: 0, bonus: 0 });
  const [viewCredits, setViewCredits] = useState(false);
  const [viewPayouts, setViewPayouts] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPayoutsCount, setTotalPayoutsCount] = useState(0);

  const fetchAffiliateBalance = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/affiliate-organizations/balance`,
      );
      setBalance(res.balance);
      setTotalPayoutsByType(res.totalPayoutsByType);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchTransactions = async (creditId?: number) => {
    try {
      let url = `/api/organization/${currentOrg?.id}/affiliate-organizations/credits?per_page=10`;
      if (creditId) {
        url += `&cursor=${creditId}`;
      }
      const res = await fetcherAuth(getToken, url);
      setAffiliateCredits((prev) => [...prev, ...res.credits]);
      setTotal(res.total);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchPayouts = async (payoutId?: number) => {
    try {
      let url = `/api/organization/${currentOrg?.id}/affiliate-organizations/payouts?per_page=10`;
      if (payoutId) {
        url += `&cursor=${payoutId}`;
      }
      const res = await fetcherAuth(getToken, url);
      setAffiliatePayouts((prev) => [...prev, ...res.payouts]);
      setTotalPayoutsCount(res.total);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleCopyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    trackEvent("Affiliate Promo Code Link Copied to Clipboard");
    if (setAlert) setAlert("Copied to clipboard!", "success");
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchAffiliateBalance();
      fetchTransactions();
      fetchPayouts();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const getPayoutDescription = (type: AffiliatePayout["type"]) => {
    switch (type) {
      case "bonus":
        return "Additional bonus";
      case "cumulative":
        return "Prior payouts (cumulative)";
      case "standard":
      default:
        return "Affiliate earnings";
    }
  };

  const formatPayoutDate = (dateString: string) => {
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  // Exclude bonus payouts from unpaid balance calculation
  const totalEarnings = balance.total_commission + balance.total_bonus;
  const unpaidBalance =
    totalEarnings - totalPayoutsByType.standard - totalPayoutsByType.cumulative;

  const formatBalance = (balance: number) => {
    return Number(balance / 100)?.toFixed(2);
  };

  const CopyBlock = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 2,
            py: 1,
            bgcolor: "grey.50",
            maxWidth: 520,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontFamily: "monospace",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.primary",
            }}
          >
            {value}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => handleCopyToClipboard(value)}
          disableElevation
          sx={{
            whiteSpace: "nowrap",
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: "#f4f5f7",
            color: "text.primary",
            textTransform: "none",
            "&:hover": { bgcolor: "#e6e8eb" },
          }}
        >
          <Box
            component="i"
            className="fa-regular fa-clipboard"
            sx={{ mr: 0.5 }}
          />{" "}
          Copy
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Welcome to Bento's Affiliate Program
      </Typography>

      <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 600 }}>
        Earn {COMMISSION_PERCENTAGE}% commission on every payment made by a new
        Bento user who subscribes using your unique promo code — valid for their
        first 12 months. Promote Bento, get rewarded.{" "}
        <StyledLink
          href={AFFILIATE_TERMS_AND_CONDITIONS_LINK}
          onClick={() => trackEvent("Affiliate Terms and Conditions Clicked")}
        >
          T&Cs apply
        </StyledLink>
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 4,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left column */}
        <Box>
          <SpecialBonuses />

          <CopyBlock
            label="Your unique affiliate link"
            value={affiliateOrg.referralLink}
          />

          {affiliateOrg?.stripePromotionCode && (
            <CopyBlock
              label="Or share a coupon code (gives referrals 10% off their first purchase)"
              value={affiliateOrg.stripePromotionCode}
            />
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mb: 4,
              maxWidth: 520,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Selling Usage Rights
              </Typography>
              <StyledLink href="https://docs.google.com/forms/d/e/1FAIpQLScA75j4KjVTL-SDorbW5vPA5bkxVPEK15R2DIEvOyKJVpwmsg/viewform">
                Complete this form
              </StyledLink>{" "}
              if you'd like to license your content about Bento for ads and earn
              up to <strong>$500</strong>.
            </Box>

            {!affiliateOrg?.stripePromotionCode && (
              <Box>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  Affiliate Partner Coupon Code
                </Typography>
                <StyledLink href="https://docs.google.com/forms/d/e/1FAIpQLSfcgUO62Tgt2aeS2E0XGbjwmcyK90t1p_PLZfrKkJRjBT0ChQ/viewform?usp=dialog">
                  Complete this form
                </StyledLink>{" "}
                to get a coupon code for your referrals. You'll still earn 10%
                of the purchase price.
              </Box>
            )}
          </Box>
        </Box>

        {/* Right column — balance cards */}
        <Box
          sx={{
            width: { xs: "100%", md: 600 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: "#f9fbfc",
              borderRadius: 3,
              p: 3,
            }}
          >
            <Typography variant="h6" gutterBottom>
              <Box
                component="i"
                className="fa-solid fa-dollar-sign"
                sx={{ color: "green.main" }}
              />{" "}
              Current Balance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {unpaidBalance < 0 ? (
                <>
                  We overpaid you in a previous payout. Your next payout will be
                  issued once your balance reaches $100 again.{" "}
                </>
              ) : (
                <>
                  You must have a minimum of $100 in unpaid commissions before
                  being eligible for a payout.{" "}
                </>
              )}
              Your earnings will be paid per the{" "}
              <Link
                target="_blank"
                rel="noreferrer"
                href={AFFILIATE_TERMS_AND_CONDITIONS_LINK}
                sx={{ color: "inherit" }}
                onClick={() =>
                  trackEvent("Affiliate Terms and Conditions Clicked")
                }
              >
                T&Cs
              </Link>
              .
            </Typography>

            {fetchLoading ? (
              <Skeleton height={60} width="40%" sx={{ my: 1 }} />
            ) : (
              <Box
                sx={{ display: "flex", alignItems: "baseline", gap: 1, my: 2 }}
              >
                <Typography variant="h2">
                  ${formatBalance(unpaidBalance)}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  USD
                </Typography>
              </Box>
            )}

            {totalPayoutsByType.standard + totalPayoutsByType.cumulative >
              0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                You've received{" "}
                <strong>
                  $
                  {formatBalance(
                    totalPayoutsByType.standard + totalPayoutsByType.cumulative,
                  )}
                </strong>{" "}
                in affiliate commissions and bonuses
                {totalPayoutsByType.bonus > 0 && (
                  <>
                    , plus{" "}
                    <strong>${formatBalance(totalPayoutsByType.bonus)}</strong>{" "}
                    in other bonuses
                  </>
                )}
                .
              </Typography>
            )}

            {affiliatePayouts?.length > 0 && (
              <Link
                sx={{ cursor: "pointer", textDecoration: "none", fontSize: 12 }}
                onClick={() => {
                  setViewPayouts((prev) => !prev);
                  trackEvent("My Affiliate Payouts Button Clicked");
                }}
              >
                <strong>{viewPayouts ? "Hide Payouts" : "See Payouts"}</strong>
              </Link>
            )}

            {viewPayouts && affiliatePayouts && (
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {affiliatePayouts.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell>{formatPayoutDate(row.date)}</TableCell>
                      <TableCell>{getPayoutDescription(row.type)}</TableCell>
                      <TableCell>${formatBalance(row.amountCents)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {totalPayoutsCount > affiliatePayouts?.length && viewPayouts && (
              <Button
                onClick={() =>
                  fetchPayouts(
                    affiliatePayouts?.[affiliatePayouts?.length - 1]?.id,
                  )
                }
                size="small"
                sx={{ fontSize: 12, textTransform: "none", mt: 1 }}
                color="secondary"
              >
                View more
              </Button>
            )}
          </Box>

          <Box
            sx={{
              bgcolor: "#f9fbfc",
              borderRadius: 3,
              p: 3,
            }}
          >
            <Typography variant="h6" gutterBottom>
              <Box
                component="i"
                className="fa-solid fa-dollar-sign"
                sx={{ color: "#f8700c" }}
              />{" "}
              Lifetime Earnings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your lifetime commissions and bonuses earned from referrals.
            </Typography>

            {fetchLoading ? (
              <Skeleton height={60} width="40%" sx={{ my: 1 }} />
            ) : (
              <Box
                sx={{ display: "flex", alignItems: "baseline", gap: 1, my: 2 }}
              >
                <Typography variant="h2">
                  $
                  {formatBalance(
                    balance.total_commission + balance.total_bonus,
                  )}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  USD
                </Typography>
              </Box>
            )}

            {affiliateCredits?.length > 0 && (
              <Link
                sx={{ cursor: "pointer", textDecoration: "none", fontSize: 12 }}
                onClick={() => {
                  setViewCredits((prev) => !prev);
                  trackEvent("My Affiliate Credits Button Clicked");
                }}
              >
                <strong>{viewCredits ? "Hide" : "See Breakdown"}</strong>
              </Link>
            )}

            {viewCredits && affiliateCredits && (
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Commission</TableCell>
                    <TableCell>Bonus</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {affiliateCredits.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell>
                        {formatEpochTimestamp({
                          epochTimestamp: row.createdAt,
                          addYear: true,
                        })}
                      </TableCell>
                      <TableCell>User paid an invoice</TableCell>
                      <TableCell>
                        ${formatBalance(row.commissionAmountCents)}
                      </TableCell>
                      <TableCell>
                        ${formatBalance(row.bonusCents || 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {total > affiliateCredits?.length && viewCredits && (
              <Button
                onClick={() =>
                  fetchTransactions(
                    affiliateCredits?.[affiliateCredits?.length - 1]?.id,
                  )
                }
                size="small"
                sx={{ fontSize: 12, textTransform: "none", mt: 1 }}
                color="secondary"
              >
                View more
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
